import { LeftOutlined, SearchOutlined } from "@ant-design/icons";
import GroupsHeaderItem from "@Components/groups/GroupsHeaderItem";
import GroupsListGroupItem from "@Components/groups/GroupsListGroupItem";
import React from "react";
import { getListStateGroup, IGroupEntry, IListState, INavState, ITag } from "@Components/groups/GroupUtils";
import GroupUserList from "@Components/groups/GroupUserList";
import { IAddressDetailsPaged } from "@Models/IAddressDetails";
import { ILicenseDetailsPaged } from "@Models/ILicenseDetails";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { GroupContext } from "@Constants";
import GroupsHeaderRoot from "@Components/groups/GroupsHeaderRoot";
import { Avatar, Tooltip } from "antd";
import MiniSpinner from "@Components/shared/MiniSpinner";
import { IAddAction, IDeleteAction } from "./types";

/**
 * Render groups only at the base level available to the user.
 * @param navState
 * @param groups
 * @param index
 * @param handleGroupSelect
 * @param handleUserSelect
 * @param sortGroupVisibility
 */
const renderBaseLevel = (
    navState: INavState,
    groups: IGroupEntry[],
    _index: number,
    handleGroupSelect: (groupEntry: IGroupEntry) => void,
    _handleUserSelect: (userId: string) => void,
    sortGroupVisibility: () => Promise<void>
): JSX.Element => {
    const groupElements: JSX.Element[] = [];
    groupElements.push(
        <div className="groups-list-list" key={"groups-items-list"}>
            {renderGroupItems(groups, -1, handleGroupSelect)}
        </div>
    );

    return (
        <div
            key={"groups-level-base"}
            id={"groups-level-base"}
            className="groups-list-scrollable"
            style={
                -1 <= navState.currentLevel
                    ? { left: 0, transition: "left .2s", overflow: "hidden scroll" }
                    : { left: "100%", transition: "left .2s", overflow: "hidden" }
            }
            onTransitionEnd={() => sortGroupVisibility}
        >
            {groupElements}
        </div>
    );
};

/**
 * Render groups at a given level.
 * @param listState
 * @param navState
 * @param level
 * @param index
 * @param handleGroupSelect
 * @param searchActive
 * @param handleUserSelect
 */
const renderLevel = (
    listState: IListState,
    navState: INavState,
    level: number,
    index: number,
    handleGroupSelect: (groupEntry: IGroupEntry) => void,
    searchActive: boolean,
    handleUserSelect: (userId: string) => void
): JSX.Element => {
    const groupElements: JSX.Element[] = [];
    listState.groups
        .filter((x) => +x.level === +level)
        .forEach((group, index) => {
            const children: IGroupEntry[] = [];
            group.children.forEach((childId) => {
                const childGroup = getListStateGroup(listState, childId);
                if (childGroup !== null) {
                    childGroup.level = level + 1;
                    children.push(childGroup);
                }
            });
            groupElements.push(
                <div
                    key={"level-" + level + "-group-" + group.groupId + "-i-" + index}
                    id={"level-" + level + "-group-" + group.groupId}
                    className="groups-list-list"
                    hidden={!group.visibility && group.groupId !== navState.currentGroupId}
                >
                    <GroupUserList
                        navState={navState}
                        searchActive={searchActive}
                        group={group}
                        handleUserSelect={handleUserSelect}
                        groupItems={renderGroupItems(children, level + 1, handleGroupSelect)}
                    />
                </div>
            );
        });

    const transitionClass = level <= navState.currentLevel ? "group-transition-left" : "group-transition-right";

    return (
        <div key={index} id={"groups-level-" + level} className={"groups-list-scrollable " + transitionClass}>
            {groupElements}
        </div>
    );
};

/**
 * Render the group header item, including any tags and controls.
 */
export const renderSearchHeader = (clearSearch: () => void, searching: boolean) => {
    return (
        <div
            id="groups-list-header"
            onClick={(e) => {
                clearSearch();
            }}
        >
            <div id="groups-list-back-button" className="back-button">
                <LeftOutlined className="groups-list-back-icon" />
            </div>
            <GroupsHeaderRoot
                title={
                    <>
                        <Avatar
                            size="small"
                            className={"group-icon"}
                            style={{ marginRight: 10 }}
                            icon={<SearchOutlined />}
                        />
                        Search &nbsp;&nbsp;
                        {searching && (
                            <span
                                key={"spin-item-searching"}
                                style={{ display: "inline-block", justifyContent: "left", paddingLeft: 10 }}
                            >
                                <MiniSpinner />
                            </span>
                        )}
                    </>
                }
                color={"#555"}
                addAction={null}
            />
        </div>
    );
};

/**
 * Render the group header item, including any tags and controls.
 */
export const renderGroupHeader = (
    listState: IListState,
    navState: INavState,
    fetchingGroups: boolean,
    fetchingTags: boolean,
    handleGroupBack: (e: React.MouseEvent<HTMLDivElement>) => void,
    addAction: IAddAction,
    deleteAction: IDeleteAction,
    groupTags: ITag[],
    addressAssignments: IAddressDetailsPaged[],
    _licenseAssignments: ILicenseDetailsPaged[],
    creditAssignments: ICreditDetailsPaged[]
) => {
    const index = listState.groups.findIndex((x) => x.groupId === navState.currentGroupId);
    return (
        <div id="groups-list-header">
            <Tooltip title="Back">
                <div
                    id="groups-list-back-button"
                    className={navState.currentLevel === -1 ? "back-button-muted" : "back-button"}
                    onClick={(e) => {
                        handleGroupBack(e);
                    }}
                >
                    <LeftOutlined className="groups-list-back-icon" />
                </div>
            </Tooltip>
            {!fetchingGroups &&
                (index !== -1 ? (
                    <GroupsHeaderItem
                        addressAssignments={addressAssignments.filter(
                            (x) => x.contextId === GroupContext && x.instanceId === listState.groups[index].groupId
                        )}
                        creditAssignments={creditAssignments.filter(
                            (x) => x.contextId === GroupContext && x.instanceId === listState.groups[index].groupId
                        )}
                        group={listState.groups[index]}
                        groupTags={groupTags}
                        fetching={fetchingTags}
                        addAction={addAction}
                        deleteAction={deleteAction}
                    />
                ) : (
                    <GroupsHeaderRoot title={<>No group selected</>} color={"#ccc"} addAction={addAction} />
                ))}
        </div>
    );
};

/**
 * Render group items at each level.
 */
export const renderGroups = (
    listState: IListState,
    navState: INavState,
    handleGroupSelect: (groupEntry: IGroupEntry) => void,
    handleUserSelect: (userId: string) => void,
    sortGroupVisibility: () => Promise<void>,
    searchActive: boolean
): JSX.Element[] => {
    const result: JSX.Element[] = [];
    const maxLevel = Math.max(...listState.groups.map((x) => x.level)) + 1;
    let index = 0;
    const baseGroups = listState.groups.filter((x) => x.level === 0);
    result.push(renderBaseLevel(navState, baseGroups, index, handleGroupSelect, handleUserSelect, sortGroupVisibility));
    index++;
    for (let level = 0; level < maxLevel; level++) {
        result.push(renderLevel(listState, navState, level, index, handleGroupSelect, searchActive, handleUserSelect));
        index++;
    }
    return result;
};

/**
 * Render group items.
 */
const renderGroupItems = (
    groups: IGroupEntry[],
    level: number,
    handleGroupSelect: (groupEntry: IGroupEntry) => void
): JSX.Element[] => {
    const groupItems: JSX.Element[] = [];
    groups.forEach((group, index) => {
        groupItems.push(
            <GroupsListGroupItem
                key={"groups-list-group-" + group.groupId + "-item-" + index}
                group={group}
                level={level}
                handleGroupSelect={handleGroupSelect}
            />
        );
    });
    return groupItems;
};
